import { useState } from "react";
import { useForm } from "react-hook-form";
import { Box } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import { DynamicForm } from "../../dynamicForm";
import { AddDialogTrigger } from "../../dynamicForm/shared/AddDialogTrigger";
import type { DocumentSourceFormValues } from "../../shared/DocumentSourceFields";
import {
  DocumentSourceFields,
  toDocumentSourcePayload,
  toDocumentSourceFormValues,
} from "../../shared/DocumentSourceFields";
import { FileField } from "../../shared/FileField";
import type { DocumentReference } from "../../dynamicForm/types";
import { toFileReference } from "../../dynamicForm/types";
import { DocumentItem } from "../../shared/DocumentItem";
import { ErrorMessage } from "../../dynamicForm/inlineForm/ErrorMessage";
import { ConfirmDeleteDialog } from "../../shared/ConfirmDeleteDialog";
import * as FormDialog from "../../shared/FormDialog";
import { updateDpiaMutationOptions } from "../../api/dpia";
import { hasErrorCode } from "../../api/errors";
import type { DpiaById } from "../../api/dpia.types";

type ExternalDpia = Extract<DpiaById, { type: "external" }>;

const hasDocument = (dpia: ExternalDpia) => dpia.kind === "file" || Boolean(dpia.url);

const toLinkReference = (url = ""): DocumentReference => ({ label: url, url });

export const DpiaDocumentField = ({ dpia, isReadOnly }: { dpia: ExternalDpia; isReadOnly: boolean }) => {
  const { t } = useTranslation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const updateDpiaMutation = useMutation(updateDpiaMutationOptions());
  const isFile = dpia.kind === "file";
  const clearDocumentPayload = { kind: "link" } as const;

  const handleRemoveClick = () => {
    if (isFile) {
      setIsDeleteDialogOpen(true);
      return;
    }
    updateDpiaMutation.mutate({ id: dpia.id, payload: clearDocumentPayload });
  };

  return (
    <DynamicForm.InlineFieldPrimary
      label={t("DpiaDrawer.Document.Label")}
      input={
        <Box>
          <AddDialogTrigger
            isEmpty={!hasDocument(dpia)}
            isReadOnly={isReadOnly}
            renderDialog={props => <DpiaDocumentDialog dpia={dpia} {...props} />}
            renderTrigger={({ onClick }) => (
              <DocumentItem
                reference={isFile ? toFileReference(dpia, dpia.fileName) : toLinkReference(dpia.url)}
                isReadOnly={isReadOnly}
                isRemoving={updateDpiaMutation.isPending}
                onEditClick={onClick}
                onRemoveClick={handleRemoveClick}
              />
            )}
          />
          {!isFile && updateDpiaMutation.isError && <ErrorMessage />}
          {isFile && isDeleteDialogOpen && (
            <ConfirmDeleteDialog
              isOpen
              onClose={() => setIsDeleteDialogOpen(false)}
              onDelete={() =>
                updateDpiaMutation.mutateAsync(
                  { id: dpia.id, payload: clearDocumentPayload },
                  { onSuccess: () => setIsDeleteDialogOpen(false) }
                )
              }
              headerText={t("Document.Delete.ConfirmHeader")}
              message={t("Document.Delete.ConfirmMessage", { fileName: dpia.fileName })}
              confirmButtonLabel={t("Document.Delete.ConfirmButton")}
            />
          )}
        </Box>
      }
    />
  );
};

const DpiaDocumentDialog = ({ dpia, onClose }: { dpia: ExternalDpia; onClose: () => void }) => {
  const { t } = useTranslation();
  const updateDpiaMutation = useMutation(updateDpiaMutationOptions());
  const isEditing = hasDocument(dpia);
  const defaultValues: DocumentSourceFormValues = isEditing
    ? toDocumentSourceFormValues(dpia)
    : { kind: "file", url: "", file: null };
  const { control, setFocus, handleSubmit } = useForm<DocumentSourceFormValues>({ defaultValues });

  FormDialog.useAutoFocusField(setFocus, defaultValues.kind === "link" ? "url" : null);

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Title>{isEditing ? t("Document.Dialog.EditTitle") : t("Document.Dialog.AddTitle")}</FormDialog.Title>
      <FormDialog.Form
        onSubmit={handleSubmit(values =>
          updateDpiaMutation.mutate({ id: dpia.id, payload: toDocumentSourcePayload(values) }, { onSuccess: onClose })
        )}
      >
        <FormDialog.Content>
          <FormDialog.FormError
            isError={updateDpiaMutation.isError}
            errorMessage={
              hasErrorCode(updateDpiaMutation.error, "EXTERNAL_DPIA_FILE_INVALID")
                ? t("Document.Error.FileNotSaved")
                : undefined
            }
          />
          <FormDialog.Fields>
            <DocumentSourceFields
              control={control}
              name="kind"
              savedFileName={dpia.kind === "file" ? dpia.fileName : undefined}
              linkField={
                <FormDialog.TextField
                  control={control}
                  name="url"
                  rules={{
                    validate: url => url.trim() !== "" || t("CreateDpia.Dialog.UrlError"),
                  }}
                  label={t("CreateDpia.Dialog.UrlLabel")}
                  placeholder={t("CreateDpia.Dialog.UrlPlaceholder")}
                  inputMode="url"
                  autoComplete="url"
                />
              }
              fileField={<FileField control={control} name="file" fileOwner={{ type: "Dpia", id: dpia.id }} />}
            />
          </FormDialog.Fields>
        </FormDialog.Content>
        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={updateDpiaMutation.isPending}>
            {isEditing ? t("ExternalMultipleLink.UpdateButton") : t("ExternalMultipleLink.AddButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
